import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { App, Button, Grid } from 'antd';
import RetryDataState from '../components/common/RetryDataState';
import { DATA_STATE, Skeleton, resourceStateOf } from '../components/loading';

import ContextBar from '../components/common/ContextBar';
import RecordBadge from '../components/common/RecordBadge';
import { confirmDestructive } from '../components/common/confirmDestructive';
import { Chart, preloadCharts } from '../components/charts/LazyCharts';
import { cardOwnerOf } from '../components/cards/CardVisuals';
import SourceMark from '../components/notifications/SourceMark';
import AddToDashboardModal from '../components/reports/AddToDashboardModal';
import ChartRangeControl from '../components/reports/ChartRangeControl';
import { chartPath } from '../components/reports/ChartRow';
import NameChartModal from '../components/reports/NameChartModal';
import { chartActions, deleteChartCopy } from '../components/reports/chartActions';
import useChartShare, { chartElements, elementNamed } from '../components/reports/useChartShare';
import { useCollectionContext } from '../contexts/CollectionContext';
import { useOptionalCardDefinitions } from '../contexts/CardDefinitionsContext';
import useChartRun from '../hooks/useChartRun';
import {
	CHART_SCOPE, chartRefusal, createChart, deleteChart, demoteChart, getChart, promoteChart, updateChart,
} from '../services/charts';
import { collectionCrumbs } from '../utils/breadcrumbs';
import { chartDoorUrl, wholeChartDoor } from '../utils/chartLinks';
import { rangeFromSearch, rangeToSearch } from '../utils/chartRanges';
import { formatRelativeToNow } from '../utils/formatters';
import { normalizeId } from '../utils/ids';
import { canManage, canReadCharts } from '../utils/permissions';
import { editChartPath } from './ReportsPage';
import useChartPlacements from '../hooks/useChartPlacements';

const OBJECT_ID = /^[0-9a-f]{24}$/i;

/** The one sentence for a chart that is gone, not yours or unreadable (ruled d26). */
export const CHART_GONE = 'This chart is no longer available.';

// The element a chart groups or splits by, from its card's effective
// elements, for a choice's own colours (q15): by name, as the spec names it.
// It lives with Share & export (useChartShare, A9), whose downloads draw the
// same colours; the dashboard page and useChartContexts ask it from here.
export { elementNamed };

/**
 * The chart page — `/collections/:collectionId/charts/:chartId` (plan §6.3).
 *
 * Its own context bar: the trail to Reports, the chart's name as a plain
 * title (a chart has no identity tile; the badge is for collections, cards
 * and items), a meta line — the card it counts as its badge, then "Counts the
 * items you can see" (a chart always runs as its viewer, invariant I1) and
 * when it last ran, with Refresh — **Edit** as the primary for someone who
 * may edit it, and the ⋯ the Reports row has (chartActions).
 *
 * Under the bar: the description, the **Date range** control (shown for a
 * chart that has a date; it replaces the saved range for this visit, in the
 * URL), the chart at full width through the lazy boundary, its notes, then
 * **Show table** (ruled d19), whose open state is in the URL too
 * (`?table=1`), so a link can open it.
 *
 * Two requests, side by side: the chart's own read (its name, spec and tier)
 * and ONE batch run for its numbers, which needs only the ids. "Gone", "not
 * yours" and "unreadable" read as one sentence.
 */
export default function ChartPage() {
	const { chartId: rawChartId } = useParams();
	const chartId = OBJECT_ID.test(rawChartId || '') ? rawChartId : null;
	const { collection, resolvedCollectionId, collectionError, refreshCollection } = useCollectionContext() || {};
	const collectionId = resolvedCollectionId;
	const navigate = useNavigate();
	const { modal, message } = App.useApp();
	const [searchParams, setSearchParams] = useSearchParams();
	const screens = Grid.useBreakpoint();
	const phone = screens.md === false;

	const allowed = Boolean(collection) && canReadCharts(collection);
	const isManager = Boolean(collection) && canManage(collection);
	const placements = useChartPlacements(collectionId);
	const reportsPath = `/collections/${collectionId}/reports`;
	const rangeParam = useMemo(() => rangeFromSearch(searchParams), [searchParams]);
	const tableOpen = searchParams.get('table') === '1';

	// The chart drawer's code starts downloading with the page, not after the numbers.
	useEffect(() => { preloadCharts().catch(() => {}); }, []);

	// ---------------------------------------------------------------- the chart
	const [chart, setChart] = useState(null);
	const [status, setStatus] = useState('loading'); // loading · ready · gone · error
	const [loadToken, setLoadToken] = useState(0);
	useEffect(() => {
		if (!collectionId || !allowed) return undefined;
		if (!chartId) {
			setChart(null);
			setStatus('gone');
			return undefined;
		}
		let active = true;
		setStatus('loading');
		// Another chart (a copy just made, opened from here) never shows the last one's name meanwhile.
		setChart((current) => (current && normalizeId(current._id) === chartId ? current : null));
		getChart(collectionId, chartId)
			.then((data) => { if (active) { setChart(data); setStatus('ready'); } })
			.catch((e) => {
				if (!active) return;
				setChart(null);
				const code = e?.response?.status;
				setStatus(code === 404 || code === 400 ? 'gone' : 'error');
			});
		return () => { active = false; };
	}, [collectionId, chartId, allowed, loadToken]);

	// ----------------------------------------------------------- its numbers
	const { result, loading, ranAt, refresh } = useChartRun({
		chartId, collectionId, range: rangeParam, enabled: allowed && Boolean(chartId),
	});
	// A run that says the chart is gone settles the page the same way the read does.
	const runGone = result && result.ok === false && result.error && result.error.code === 'chart_unavailable';

	// "Updated 2 minutes ago" keeps time while the page stays open.
	const [, setTick] = useState(0);
	useEffect(() => {
		const timer = setInterval(() => setTick((n) => n + 1), 30000);
		return () => clearInterval(timer);
	}, []);

	// ------------------------------------------------------- its card
	const cardDefinitions = useOptionalCardDefinitions();
	const cardId = normalizeId(chart?.spec?.card_id);
	const card = cardId ? cardDefinitions?.cardsById?.[cardId] || null : null;
	const noteMissingCards = cardDefinitions?.noteMissingCards;
	useEffect(() => {
		if (cardId && !card && typeof noteMissingCards === 'function') noteMissingCards([cardId]);
	}, [cardId, card, noteMissingCards]);
	const getElementsForCard = cardDefinitions?.getElementsForCard;
	// The card's elements and its chain's, which say a spec field held by id (KPR-238 P6).
	const allCardsById = cardDefinitions?.cardsById;
	const elementSetsById = cardDefinitions?.elementSetsById;
	const elements = useMemo(
		() => chartElements(chart, getElementsForCard, { cardsById: allCardsById, elementSetsById }),
		[chart, getElementsForCard, allCardsById, elementSetsById],
	);

	// ------------------------------------------------------- what it may do
	const [naming, setNaming] = useState(null);
	const [placing, setPlacing] = useState(false);
	// Share & export (A9): Download image draws the answer on screen; Download
	// data runs the chart again at the export grade, with this visit's range;
	// Copy link carries this visit's address (its range, an open table).
	const share = useChartShare({ collectionId });
	let imageReason = null;
	if (share.busy === 'image') imageReason = 'Downloading the image…';
	else if (!result || loading) imageReason = 'The chart is still running.';
	else if (result.ok === false) imageReason = "The chart didn't run, so there's nothing to draw.";
	const dataReason = share.busy === 'data' ? 'Downloading the data…' : null;
	// The link carries what the page shows — its range and an open table —
	// built from that state, never the address as typed (anything else in it
	// would ride along into a stored short link: the A9 review).
	const linkSearch = () => {
		const next = rangeToSearch(new URLSearchParams(), rangeParam);
		if (tableOpen) next.set('table', '1');
		return next.toString();
	};
	const linkReason = share.busy === 'link' ? 'Copying the link…' : null;
	const mayEdit = chart?.canEdit === true;
	const goEdit = useCallback(() => navigate(editChartPath(collectionId, chartId)), [navigate, collectionId, chartId]);
	const whole = wholeChartDoor(result, { split: Boolean(chart?.spec?.splitBy) });
	const listUrl = whole.door ? chartDoorUrl(collectionId, whole.door) : null;
	let openListReason = null;
	if (!listUrl) {
		if (!result || loading) openListReason = 'The chart is still running.';
		else if (result.ok === false) openListReason = "The chart didn't run, so it can't open its list.";
		else openListReason = whole.reason;
	}

	const updateRange = (next) => setSearchParams(rangeToSearch(searchParams, next), { replace: true });
	const toggleTable = () => {
		const next = new URLSearchParams(searchParams);
		if (tableOpen) next.delete('table');
		else next.set('table', '1');
		setSearchParams(next, { replace: true });
	};

	const promote = async () => {
		try {
			setChart(await promoteChart(collectionId, chartId));
		} catch (e) {
			message.error(chartRefusal(e, 'Could not pin the chart for everyone.'));
		}
	};
	const demote = async () => {
		try {
			setChart(await demoteChart(collectionId, chartId));
		} catch (e) {
			message.error(chartRefusal(e, 'Could not unpin the chart.'));
		}
	};
	const confirmDelete = () => {
		confirmDestructive(modal, {
			...deleteChartCopy(chart),
			okText: 'Delete',
			onOk: async () => {
				try {
					await deleteChart(collectionId, chartId);
					message.success(`Deleted "${chart?.name || 'the chart'}". History can restore it.`);
					navigate(reportsPath);
				} catch (e) {
					message.error(chartRefusal(e, 'Could not delete the chart.'));
				}
			},
		});
	};
	const submitName = async (values) => {
		if (naming?.mode === 'duplicate') {
			const { warnings, ...created } = await createChart(collectionId, { ...values, spec: chart?.spec }) || {};
			message.success(`Made "${created.name || values.name}", a copy only you see.`);
			if (created._id) navigate(chartPath(collectionId, normalizeId(created._id)));
			return;
		}
		const { warnings, ...updated } = await updateChart(collectionId, chartId, values) || {};
		setChart((current) => ({ ...current, ...updated }));
	};

	const menuActions = chart ? chartActions({
		chart,
		isManager,
		...placements.chart(chart),
		onOpenList: () => { if (listUrl) navigate(listUrl); },
		openListReason,
		onReports: () => navigate(reportsPath),
		onRename: () => setNaming({ mode: 'rename' }),
		onDuplicate: () => setNaming({ mode: 'duplicate' }),
		onAddToDashboard: () => setPlacing(true),
		onPromote: promote,
		onDemote: demote,
		onDownloadImage: () => share.downloadImage(chart, { result, range: rangeParam, elements }),
		imageReason,
		onDownloadData: () => share.downloadData(chart, { range: rangeParam, cardName: card?.name, elements }),
		dataReason,
		onCopyLink: () => share.copyLink(chart, { search: linkSearch() }),
		linkReason,
		onDelete: confirmDelete,
	}) : (allowed
		? [{ key: 'reports', group: 'navigate', glyph: 'open', label: 'Go to Reports', onClick: () => navigate(reportsPath) }]
		// Charts are not offered to a global collection's consumer or a link-only reader (d14): no door to Reports either.
		: []);

	// ------------------------------------------------------- the bar
	// A collection that is gone or not shared takes its charts with it: the
	// same one sentence (no bar of the collection's is drawn here to say it).
	const collectionGone = !collection && resourceStateOf(collectionError) === 'not-found';
	const collectionFailed = !collection && collectionError && !collectionGone;
	const gone = collectionGone || status === 'gone' || (runGone && status !== 'ready');
	let title = chart?.name;
	if (!title) title = gone || collectionFailed || status === 'error' || (collection && !allowed) ? 'Chart' : 'Loading chart…';

	// Its tier mark says the whole of it to a reader (SourceMark's sentence),
	// and the meta line's own words say it on a phone, where nothing hovers.
	// A chart is only ever its own collection's (a sub-collection inherits
	// none, KPR-1084).
	const tierMark = !chart ? null : chart.scope === CHART_SCOPE.COLLECTION ? (
		<SourceMark word='Pinned for everyone' phrase='Pinned for everyone by a collection manager' className='chart-page__tier' />
	) : (
		<SourceMark word='Yours' phrase='Only you see this chart' own className='chart-page__tier' />
	);

	let updated = null;
	if (loading) updated = 'Running…';
	else if (ranAt) updated = `Updated ${formatRelativeToNow(ranAt)}`;

	const below = chart ? (
		<span className='chart-page__meta'>
			{chart.spec && chart.spec.card_id === null ? (
				<span className='chart-page__every-card'>Every card</span>
			) : card ? (
				// A parent's or a lender's card says whose it is (standards §1, qualified).
				<RecordBadge kind='card' record={card} title={card.name || 'Card'} size='sm' collection={cardOwnerOf(card)} className='chart-page__card' />
			) : null}
			{tierMark}
			<span className='chart-page__fact'>Counts the items you can see</span>
			{updated ? <span className='chart-page__fact' aria-live='polite'>{updated}</span> : null}
			<Button type='link' size='small' className='chart-page__refresh' onClick={refresh} disabled={loading}>Refresh</Button>
		</span>
	) : null;

	const bar = (
		<ContextBar
			breadcrumb={collectionCrumbs(collection, collectionId, { label: 'Reports', to: reportsPath })}
			title={title}
			below={below}
			actions={mayEdit ? <Button type='primary' onClick={goEdit}>Edit</Button> : null}
			menuActions={menuActions}
		/>
	);

	// ------------------------------------------------------- the body
	let body;
	if (collection && !allowed) {
		body = <p className='chart-page__empty'>Charts are for people who read this collection themselves, so there is none to show here.</p>;
	} else if (gone) {
		body = (
			<p className='chart-page__empty'>
				{`${CHART_GONE} `}
				<Link to={reportsPath}>Go to Reports</Link>
			</p>
		);
	} else if (status === 'error' || collectionFailed) {
		body = (
			<div className='chart-page__error' role='alert'>
				<span>This chart couldn't be loaded. Trying again may help.</span>
				<Button size='small' onClick={() => (collectionFailed ? refreshCollection?.() : setLoadToken((n) => n + 1))}>Try again</Button>
			</div>
		);
	} else if (!chart) {
		body = (
			<RetryDataState
				state={DATA_STATE.LOADING}
				skeleton={<div className='chart-page__skeleton' style={{ height: phone ? 240 : 360 }}><Skeleton variant='chart-frame' /></div>}
				onRetry={() => setLoadToken((n) => n + 1)}
				label='this chart'
			/>
		);
	} else {
		body = (
			<>
				{/* Paper has no context bar (print.css): the chart's name and whose numbers they are, printed only. */}
				<div className='chart-page__print-head' aria-hidden='true'>
					<div className='chart-page__print-title'>{chart.name}</div>
					<div className='chart-page__print-meta'>
						{[collection?.name, card?.name || (chart.spec && chart.spec.card_id === null ? 'Every card' : null), 'Counts the items you can see', updated]
							.filter(Boolean).join(' · ')}
					</div>
				</div>
				{chart.description ? <p className='chart-page__description'>{chart.description}</p> : null}
				{chart.spec && chart.spec.time ? (
					<ChartRangeControl saved={chart.spec.time.range ?? null} value={rangeParam} onChange={updateRange} phone={phone} />
				) : null}
				<Chart
					chart={chart}
					result={result}
					loading={loading}
					collectionId={collectionId}
					cardName={card?.name}
					elements={elements}
					showTitle={false}
					headingLevel={2}
					height={phone ? 240 : 360}
					showTable={tableOpen}
					onToggleTable={toggleTable}
					onEdit={mayEdit ? goEdit : undefined}
					onRetry={refresh}
				/>
			</>
		);
	}

	return (
		<div className='chart-page'>
			{bar}
			<div className='chart-page__body'>{body}</div>
			{share.modal}
			{chart ? (
				<NameChartModal
					open={Boolean(naming)}
					mode={naming?.mode}
					chart={chart}
					onClose={() => setNaming(null)}
					onSubmit={submitName}
				/>
			) : null}
			{chart ? (
				<AddToDashboardModal
					open={placing}
					chart={chart}
					collectionId={collectionId}
					onClose={() => setPlacing(false)}
				/>
			) : null}
		</div>
	);
}
